import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Icon from '../../components/AppIcon';
import CorporateSelect from '../../components/ui/CorporateSelect';
import { marcasService, proveedoresService } from '../../services/tiendaService';
import { tiendaDb } from '../../lib/supabase';
import { calcularPvpTeorico, calcularPvpSugerido, calcularMargenReal, calcularCosteConIva, calcularCosteConImpuestos, calcularEstadosProducto, productosService, buscarDuplicados } from '../../services/productService';

// ─── CONSTANTES ───────────────────────────────────────────────────────────────
const ACCENT = '#34D399';
const GOLD = '#D6EF4E';
const BLUE = '#60A5FA';
const PURPLE = '#A78BFA';
const ORANGE = '#FB923C';
const RED = '#F87171';
const CYAN = '#22D3EE';
const MARGEN_DEFAULT = 35;
const RECARGO_DEFAULT = 1.4;

const fmt = (n, d = 2) => typeof n === 'number' && isFinite(n) ? n?.toLocaleString('es-ES', { minimumFractionDigits: d, maximumFractionDigits: d }) : '—';
const fmtEur = (n) => typeof n === 'number' && isFinite(n) && n > 0 ? `${fmt(n)} €` : '—';
const fmtPct = (n) => typeof n === 'number' && isFinite(n) ? `${fmt(n, 1)}%` : '—';

const inputSt = { background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)' };
const inputCls = 'w-full px-3 py-2 rounded-xl text-sm font-oranienbaum text-white/85 outline-none';

const REGLA_OPTS = [
  { value: 'TERMINAR_99', label: 'Terminar en ,99' },
  { value: 'TERMINAR_95', label: 'Terminar en ,95' },
  { value: 'TERMINAR_90', label: 'Terminar en ,90' },
  { value: 'MULTIPLO_05', label: 'Múltiplos de 0,05' },
  { value: 'MULTIPLO_10', label: 'Múltiplos de 0,10' },
  { value: 'SIN_REDONDEO', label: 'Sin redondeo' },
];

const IVA_OPTS = [
  { value: '0', label: '0%' },
  { value: '4', label: '4%' },
  { value: '10', label: '10%' },
  { value: '21', label: '21%' },
];

// ─── HELPERS DE MARGEN ────────────────────────────────────────────────────────
// margen_objetivo se guarda como decimal (0.35 = 35%)
const getMargenEfectivo = (p) => {
  if (p?.margen_objetivo != null) return p?.margen_objetivo; // decimal
  if (p?.familia_margen_objetivo != null) return p?.familia_margen_objetivo; // decimal
  return MARGEN_DEFAULT / 100;
};

const getReglaRedondeo = (p) => p?.familia_regla_redondeo || 'TERMINAR_99';

const getMargenRealDisplay = (p) => {
  const coste = p?.precio_compra;
  const pvp = p?.pvp_venta;
  if (!coste || coste <= 0 || !pvp || pvp <= 0) return null;
  return calcularMargenReal(pvp, coste); // returns percentage
};

const getPvpSugeridoCalc = (p) => {
  const coste = p?.precio_compra;
  if (!coste || coste <= 0) return null;
  const margen = getMargenEfectivo(p);
  const iva = p?.tipo_iva ?? 10;
  const regla = getReglaRedondeo(p);
  return calcularPvpSugerido(coste, margen, iva, regla);
};

// ─── BADGE EPELSA SYNC ────────────────────────────────────────────────────────
const EpelsaSyncBadge = ({ status }) => {
  if (!status || status === 'SIN_CAMBIOS') return null;
  const cfg = {
    PENDIENTE_ALTA: { label: '🟡 Pendiente alta', color: '#D6EF4E' },
    PENDIENTE_ACTUALIZACION: { label: '🟠 Pendiente actualiz.', color: '#FB923C' },
    PENDIENTE_EPELSA: { label: '🟡 Pendiente Epelsa', color: '#D6EF4E' },
    SINCRONIZADO: { label: '🟢 Sincronizado', color: '#34D399' },
    ERROR: { label: '🔴 Error Epelsa', color: '#F87171' },
  };
  const c = cfg?.[status];
  if (!c) return null;
  return (
    <span className="text-[9px] font-oranienbaum px-1.5 py-0.5 rounded-full whitespace-nowrap" style={{ background: `${c?.color}15`, color: c?.color, border: `1px solid ${c?.color}30` }}>
      {c?.label}
    </span>
  );
};

// ─── BADGE DE ESTADO ──────────────────────────────────────────────────────────
const EstadoBadge = ({ estados }) => {
  if (!estados || estados?.length === 0) return null;
  const priority = ['SIN_COSTE', 'PVP_BAJO_COSTE', 'STOCK_NEGATIVO', 'MARGEN_BAJO', 'SIN_PROVEEDOR', 'SIN_FAMILIA', 'SIN_EAN', 'PRECIO_BLOQUEADO', 'PRODUCTO_INACTIVO', 'MARGEN_ALTO'];
  const top = priority?.find(s => estados?.includes(s));
  if (!top) return null;
  const cfg = {
    SIN_COSTE: { label: 'Sin coste', color: RED },
    PVP_BAJO_COSTE: { label: 'PVP < coste', color: RED },
    STOCK_NEGATIVO: { label: 'Stock neg.', color: RED },
    MARGEN_BAJO: { label: 'Margen bajo', color: ORANGE },
    SIN_PROVEEDOR: { label: 'Sin prov.', color: ORANGE },
    SIN_FAMILIA: { label: 'Sin familia', color: ORANGE },
    SIN_EAN: { label: 'Sin EAN', color: 'rgba(255,255,255,0.35)' },
    PRECIO_BLOQUEADO: { label: 'PVP bloq.', color: BLUE },
    PRODUCTO_INACTIVO: { label: 'Inactivo', color: 'rgba(255,255,255,0.25)' },
    MARGEN_ALTO: { label: 'Margen alto', color: ACCENT },
  };
  const c = cfg?.[top];
  return (
    <span className="text-[9px] font-oranienbaum px-1.5 py-0.5 rounded-full" style={{ background: `${c?.color}15`, color: c?.color, border: `1px solid ${c?.color}30` }}>
      {c?.label}
    </span>
  );
};

// ─── MODAL HISTÓRICO COSTES ───────────────────────────────────────────────────
const HistoricoModal = ({ producto, onClose }) => {
  const [historico, setHistorico] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const data = await productosService?.getHistoricoCoste(producto?.id);
        setHistorico(data);
      } catch (e) { console.error(e); }
      setLoading(false);
    })();
  }, [producto?.id]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.85)' }}>
      <div className="w-full max-w-3xl rounded-2xl flex flex-col max-h-[85vh]" style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.10)' }}>
        <div className="flex items-center justify-between px-6 py-4 flex-shrink-0" style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${PURPLE}15`, border: `1px solid ${PURPLE}30` }}>
              <Icon name="History" size={15} style={{ color: PURPLE }} />
            </div>
            <div>
              <h3 className="font-oranienbaum font-bold text-white text-lg">Histórico de Costes</h3>
              <p className="font-oranienbaum text-white/40 text-xs">{producto?.nombre}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-white/40 hover:text-white/80"><Icon name="X" size={16} /></button>
        </div>
        <div className="overflow-y-auto flex-1 px-6 py-4">
          {loading ? (
            <div className="flex items-center justify-center py-12"><div className="w-5 h-5 rounded-full border-2 border-white/10 border-t-white/60 animate-spin" /></div>
          ) : historico?.length === 0 ? (
            <div className="text-center py-12">
              <Icon name="History" size={32} className="text-white/10 mx-auto mb-2" />
              <p className="font-oranienbaum text-white/30 text-sm">Sin histórico de costes</p>
            </div>
          ) : (
            <table className="w-full">
              <thead>
                <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                  {['Fecha', 'Proveedor', 'Coste s/IVA', 'IVA %', 'Recargo %', 'Coste c/Imp.', 'PVP sugerido', 'PVP actual', 'Origen']?.map(h => (
                    <th key={h} className="pb-2 text-left font-oranienbaum text-white/30 text-xs uppercase tracking-wider pr-3">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {historico?.map(h => (
                  <tr key={h?.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                    <td className="py-2.5 pr-3 font-oranienbaum text-white/70 text-xs">{h?.fecha}</td>
                    <td className="py-2.5 pr-3 font-oranienbaum text-white/60 text-xs">{h?.proveedor?.nombre || '—'}</td>
                    <td className="py-2.5 pr-3 font-oranienbaum text-white/80 text-xs">{fmtEur(h?.precio_neto)}</td>
                    <td className="py-2.5 pr-3 font-oranienbaum text-white/50 text-xs">{h?.iva_porcentaje ?? '—'}%</td>
                    <td className="py-2.5 pr-3 font-oranienbaum text-white/50 text-xs">{h?.recargo_equivalencia_pct ?? '—'}%</td>
                    <td className="py-2.5 pr-3 font-oranienbaum text-xs" style={{ color: PURPLE }}>{fmtEur(h?.coste_con_impuestos)}</td>
                    <td className="py-2.5 pr-3 font-oranienbaum text-xs" style={{ color: GOLD }}>{fmtEur(h?.pvp_sugerido)}</td>
                    <td className="py-2.5 pr-3 font-oranienbaum text-xs" style={{ color: ACCENT }}>{fmtEur(h?.pvp_actual)}</td>
                    <td className="py-2.5 pr-3 font-oranienbaum text-white/30 text-xs">{h?.origen || 'MANUAL'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── FAMILIAS EPELSA (para el selector en el modal) ──────────────────────────
const EPELSA_FAMILIAS = [
  { code: 1,  nombre: 'Jamones' },
  { code: 2,  nombre: 'Paletillas' },
  { code: 3,  nombre: 'Quesos' },
  { code: 4,  nombre: 'Loncheados' },
  { code: 5,  nombre: 'Embutidos' },
  { code: 6,  nombre: 'Aceites' },
  { code: 7,  nombre: 'Conservas' },
  { code: 8,  nombre: 'Vinos' },
  { code: 9,  nombre: 'Bebidas' },
  { code: 10, nombre: 'Miel' },
  { code: 11, nombre: 'Bebidas Alcohólicas' },
  { code: 12, nombre: 'Cortes' },
  { code: 13, nombre: 'Varios' },
  { code: 14, nombre: 'Aceitunas' },
  { code: 15, nombre: 'Sal' },
  { code: 16, nombre: 'Vino Granel' },
  { code: 17, nombre: 'Pan' },
  { code: 18, nombre: 'Postres' },
  { code: 19, nombre: 'Del Mar' },
  { code: 20, nombre: 'Pack Jamones' },
  { code: 21, nombre: 'Carnes Ibéricas' },
];

const EPELSA_FAMILIA_OPTS = [
  { value: '', label: '— Sin mapeo Epelsa —' },
  ...EPELSA_FAMILIAS?.map(f => ({
    value: String(f?.code),
    label: `${f?.code} · ${f?.nombre} (rango ${f?.code * 100}–${f?.code * 100 + 99})`,
  })),
];

// ─── MODAL GESTIÓN FAMILIAS ───────────────────────────────────────────────────
const GestionFamiliasModal = ({ onClose, onRefresh }) => {
  const [familias, setFamilias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState({
    nombre: '',
    descripcion: '',
    margen_objetivo: '',
    regla_redondeo: 'TERMINAR_99',
    activo: true,
    epelsa_family_code: '',
    epelsa_sync_enabled: false,
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const [conteos, setConteos] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: fams } = await tiendaDb?.from('familias_producto')
        ?.select('*')
        ?.order('orden', { nullsFirst: false })
        ?.order('nombre');
      setFamilias(fams || []);
      const { data: prods } = await tiendaDb?.from('productos')?.select('familia_id')?.not('familia_id', 'is', null);
      const cnt = {};
      (prods || [])?.forEach(p => { cnt[p.familia_id] = (cnt?.[p?.familia_id] || 0) + 1; });
      setConteos(cnt);
    } catch (e) { setErr(e?.message); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const startEdit = (f) => {
    setEditando(f?.id);
    setForm({
      nombre: f?.nombre,
      descripcion: f?.descripcion || '',
      margen_objetivo: f?.margen_objetivo != null ? (f?.margen_objetivo * 100)?.toFixed(1) : '',
      regla_redondeo: f?.regla_redondeo || 'TERMINAR_99',
      activo: f?.activo ?? true,
      epelsa_family_code: f?.epelsa_family_code != null ? String(f?.epelsa_family_code) : '',
      epelsa_sync_enabled: f?.epelsa_sync_enabled ?? false,
    });
    setErr(null);
  };

  const startNew = () => {
    setEditando('NEW');
    setForm({ nombre: '', descripcion: '', margen_objetivo: '', regla_redondeo: 'TERMINAR_99', activo: true, epelsa_family_code: '', epelsa_sync_enabled: false });
    setErr(null);
  };

  const handleSave = async () => {
    if (!form?.nombre?.trim()) { setErr('El nombre es obligatorio'); return; }
    setSaving(true);
    setErr(null);
    try {
      const epelsaCode = form?.epelsa_family_code !== '' ? parseInt(form?.epelsa_family_code, 10) : null;
      const payload = {
        nombre: form?.nombre?.trim(),
        descripcion: form?.descripcion || null,
        margen_objetivo: form?.margen_objetivo !== '' ? parseFloat(form?.margen_objetivo) / 100 : null,
        regla_redondeo: form?.regla_redondeo || 'TERMINAR_99',
        activo: form?.activo,
        epelsa_family_code: epelsaCode,
        epelsa_sync_enabled: form?.epelsa_sync_enabled,
        updated_at: new Date()?.toISOString(),
      };
      if (editando === 'NEW') {
        await tiendaDb?.from('familias_producto')?.insert(payload);
      } else {
        await tiendaDb?.from('familias_producto')?.update(payload)?.eq('id', editando);
      }
      setEditando(null);
      await load();
      onRefresh?.();
    } catch (e) { setErr(e?.message); }
    setSaving(false);
  };

  const handleToggleActivo = async (f) => {
    await tiendaDb?.from('familias_producto')?.update({ activo: !f?.activo })?.eq('id', f?.id);
    await load();
    onRefresh?.();
  };

  const handleDelete = async (f) => {
    const cnt = conteos?.[f?.id] || 0;
    if (cnt > 0) { setErr(`No se puede eliminar "${f?.nombre}" porque tiene ${cnt} productos. Reasigna primero los productos.`); return; }
    if (!window.confirm(`¿Eliminar la familia "${f?.nombre}"?`)) return;
    await tiendaDb?.from('familias_producto')?.delete()?.eq('id', f?.id);
    await load();
    onRefresh?.();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.85)' }}>
      <div className="w-full max-w-4xl rounded-2xl flex flex-col max-h-[90vh]" style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.10)' }}>
        <div className="flex items-center justify-between px-6 py-4 flex-shrink-0" style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${GOLD}15`, border: `1px solid ${GOLD}30` }}>
              <Icon name="Tag" size={15} style={{ color: GOLD }} />
            </div>
            <div>
              <h3 className="font-oranienbaum font-bold text-white text-lg">Gestionar Familias</h3>
              <p className="font-oranienbaum text-white/30 text-xs">Incluye mapeo con familias Epelsa para sincronización automática</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={startNew} className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-oranienbaum transition-all" style={{ background: `${GOLD}15`, border: `1px solid ${GOLD}30`, color: GOLD }}>
              <Icon name="Plus" size={12} />Nueva familia
            </button>
            <button onClick={onClose} className="p-1.5 rounded-lg text-white/40 hover:text-white/80"><Icon name="X" size={16} /></button>
          </div>
        </div>

        {err && <div className="mx-6 mt-3 p-3 rounded-xl text-xs font-oranienbaum text-red-400" style={{ background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.20)' }}>{err}</div>}

        {editando && (
          <div className="mx-6 mt-4 p-4 rounded-xl flex-shrink-0" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
            <p className="font-oranienbaum text-white/50 text-xs uppercase tracking-wider mb-3">{editando === 'NEW' ? 'Nueva familia' : 'Editar familia'}</p>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className="block font-oranienbaum text-white/50 text-xs mb-1">Nombre *</label>
                <input type="text" value={form?.nombre} onChange={e => setForm(p => ({ ...p, nombre: e?.target?.value }))} className={inputCls} style={inputSt} placeholder="Nombre de la familia" />
              </div>
              <div>
                <label className="block font-oranienbaum text-white/50 text-xs mb-1">Margen por defecto (%)</label>
                <input type="number" value={form?.margen_objetivo} onChange={e => setForm(p => ({ ...p, margen_objetivo: e?.target?.value }))} className={inputCls} style={inputSt} placeholder="35" step="0.1" min="0" max="100" />
              </div>
              <div>
                <label className="block font-oranienbaum text-white/50 text-xs mb-1">Descripción</label>
                <input type="text" value={form?.descripcion} onChange={e => setForm(p => ({ ...p, descripcion: e?.target?.value }))} className={inputCls} style={inputSt} placeholder="Descripción opcional" />
              </div>
              <div>
                <label className="block font-oranienbaum text-white/50 text-xs mb-1">Regla de redondeo</label>
                <CorporateSelect value={form?.regla_redondeo} onChange={v => setForm(p => ({ ...p, regla_redondeo: v }))} options={REGLA_OPTS} />
              </div>
            </div>

            {/* Sección Epelsa */}
            <div className="p-3 rounded-xl mb-3" style={{ background: 'rgba(214,239,78,0.04)', border: '1px solid rgba(214,239,78,0.15)' }}>
              <p className="font-oranienbaum text-xs uppercase tracking-wider mb-2" style={{ color: GOLD }}>Mapeo Epelsa</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-oranienbaum text-white/50 text-xs mb-1">Familia Epelsa</label>
                  <CorporateSelect
                    value={form?.epelsa_family_code}
                    onChange={v => setForm(p => ({ ...p, epelsa_family_code: v, epelsa_sync_enabled: v !== '' }))}
                    options={EPELSA_FAMILIA_OPTS}
                    searchable
                  />
                  {form?.epelsa_family_code && (
                    <p className="font-oranienbaum text-white/30 text-[10px] mt-1">
                      Rango de códigos: {parseInt(form?.epelsa_family_code) * 100}–{parseInt(form?.epelsa_family_code) * 100 + 99}
                    </p>
                  )}
                </div>
                <div className="flex flex-col justify-center">
                  <button
                    type="button"
                    onClick={() => setForm(p => ({ ...p, epelsa_sync_enabled: !p?.epelsa_sync_enabled }))}
                    disabled={!form?.epelsa_family_code}
                    className="flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-oranienbaum transition-all disabled:opacity-40"
                    style={form?.epelsa_sync_enabled
                      ? { background: 'rgba(214,239,78,0.12)', border: '1px solid rgba(214,239,78,0.25)', color: GOLD }
                      : { background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)', color: 'rgba(255,255,255,0.40)' }}>
                    <Icon name={form?.epelsa_sync_enabled ? 'ToggleRight' : 'ToggleLeft'} size={14} />
                    {form?.epelsa_sync_enabled ? 'Sincronización Epelsa activa' : 'Sincronización Epelsa inactiva'}
                  </button>
                  <p className="font-oranienbaum text-white/25 text-[10px] mt-1.5 px-1">
                    {form?.epelsa_sync_enabled
                      ? 'Los productos de esta familia se sincronizarán automáticamente con Epelsa' :'Los productos no se enviarán a Epelsa hasta activar la sincronización'}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <button type="button" onClick={() => setForm(p => ({ ...p, activo: !p?.activo }))} className="flex items-center gap-2 px-3 py-1.5 rounded-xl text-xs font-oranienbaum transition-all" style={form?.activo ? { background: 'rgba(52,211,153,0.12)', border: '1px solid rgba(52,211,153,0.25)', color: ACCENT } : { background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)', color: 'rgba(255,255,255,0.40)' }}>
                <Icon name={form?.activo ? 'ToggleRight' : 'ToggleLeft'} size={14} />{form?.activo ? 'Activa' : 'Inactiva'}
              </button>
              <div className="flex gap-2">
                <button onClick={() => setEditando(null)} className="px-3 py-1.5 rounded-xl text-xs font-oranienbaum text-white/40 hover:text-white/70" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}>Cancelar</button>
                <button onClick={handleSave} disabled={saving} className="px-4 py-1.5 rounded-xl text-xs font-oranienbaum transition-all" style={{ background: `${GOLD}20`, border: `1px solid ${GOLD}40`, color: GOLD }}>
                  {saving ? 'Guardando...' : 'Guardar'}
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="overflow-y-auto flex-1 px-6 py-4">
          {loading ? (
            <div className="flex items-center justify-center py-12"><div className="w-6 h-6 rounded-full border-2 border-white/10 border-t-white/60 animate-spin" /></div>
          ) : (
            <table className="w-full">
              <thead>
                <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                  {['Nombre', 'Margen', 'Familia Epelsa', 'Rango Códigos', 'Sync', 'Productos', 'Estado', 'Acciones']?.map(h => (
                    <th key={h} className="pb-2 text-left font-oranienbaum text-white/30 text-xs uppercase tracking-wider pr-3">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {familias?.map(f => {
                  const epelsaFam = EPELSA_FAMILIAS?.find(e => e?.code === f?.epelsa_family_code);
                  return (
                    <tr key={f?.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                      <td className="py-2.5 pr-3">
                        <p className="font-oranienbaum text-white/85 text-sm">{f?.nombre}</p>
                        {f?.descripcion && <p className="font-oranienbaum text-white/30 text-xs">{f?.descripcion}</p>}
                      </td>
                      <td className="py-2.5 pr-3"><span className="font-oranienbaum text-sm" style={{ color: GOLD }}>{f?.margen_objetivo ? `${(f?.margen_objetivo * 100)?.toFixed(1)}%` : '—'}</span></td>
                      <td className="py-2.5 pr-3">
                        {epelsaFam ? (
                          <span className="font-oranienbaum text-xs px-2 py-0.5 rounded-full" style={{ background: `${GOLD}12`, color: GOLD, border: `1px solid ${GOLD}25` }}>
                            {f?.epelsa_family_code} · {epelsaFam?.nombre}
                          </span>
                        ) : (
                          <span className="font-oranienbaum text-white/25 text-xs">Sin mapeo</span>
                        )}
                      </td>
                      <td className="py-2.5 pr-3">
                        {f?.epelsa_family_code ? (
                          <span className="font-oranienbaum text-white/40 text-xs font-mono">
                            {f?.epelsa_family_code * 100}–{f?.epelsa_family_code * 100 + 99}
                          </span>
                        ) : <span className="text-white/20 text-xs">—</span>}
                      </td>
                      <td className="py-2.5 pr-3">
                        {f?.epelsa_sync_enabled ? (
                          <span className="font-oranienbaum text-[10px] px-1.5 py-0.5 rounded-full" style={{ background: 'rgba(52,211,153,0.10)', color: ACCENT, border: '1px solid rgba(52,211,153,0.20)' }}>✓ Activa</span>
                        ) : (
                          <span className="font-oranienbaum text-[10px] px-1.5 py-0.5 rounded-full" style={{ background: 'rgba(255,255,255,0.04)', color: 'rgba(255,255,255,0.25)', border: '1px solid rgba(255,255,255,0.08)' }}>Inactiva</span>
                        )}
                      </td>
                      <td className="py-2.5 pr-3"><span className="font-oranienbaum text-white/60 text-sm">{conteos?.[f?.id] || 0}</span></td>
                      <td className="py-2.5 pr-3">
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-oranienbaum" style={f?.activo ? { background: 'rgba(52,211,153,0.12)', color: ACCENT, border: '1px solid rgba(52,211,153,0.25)' } : { background: 'rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.30)', border: '1px solid rgba(255,255,255,0.10)' }}>
                          {f?.activo ? 'Activa' : 'Inactiva'}
                        </span>
                      </td>
                      <td className="py-2.5">
                        <div className="flex items-center gap-1">
                          <button onClick={() => startEdit(f)} className="p-1.5 rounded-lg text-white/40 hover:text-white/80 transition-colors"><Icon name="Edit2" size={13} /></button>
                          <button onClick={() => handleToggleActivo(f)} className="p-1.5 rounded-lg transition-colors" style={{ color: f?.activo ? ORANGE : ACCENT }}><Icon name={f?.activo ? 'EyeOff' : 'Eye'} size={13} /></button>
                          <button onClick={() => handleDelete(f)} className="p-1.5 rounded-lg text-white/30 hover:text-red-400 transition-colors"><Icon name="Trash2" size={13} /></button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── MODAL EDICIÓN MASIVA ─────────────────────────────────────────────────────
const BulkEditModal = ({ selectedIds, productos, familias, proveedores, onSave, onClose }) => {
  const [fields, setFields] = useState({
    familia_id: { enabled: false, value: '' },
    proveedor_principal_id: { enabled: false, value: '' },
    tipo_iva: { enabled: false, value: '10' },
    recargo_equivalencia: { enabled: false, value: String(RECARGO_DEFAULT) },
    margen_objetivo: { enabled: false, value: String(MARGEN_DEFAULT) },
    limpiar_margen: { enabled: false, value: false },
    aplicar_pvp_sugerido: { enabled: false, value: false },
    activo: { enabled: false, value: 'true' },
    bloqueo_precio: { enabled: false, value: 'false' },
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const [confirmed, setConfirmed] = useState(false);

  const toggle = (key) => setFields(p => ({ ...p, [key]: { ...p?.[key], enabled: !p?.[key]?.enabled } }));
  const setVal = (key, value) => setFields(p => ({ ...p, [key]: { ...p?.[key], value } }));
  const enabledCount = Object.values(fields)?.filter(f => f?.enabled)?.length;

  const handleApply = async () => {
    if (!confirmed) { setConfirmed(true); return; }
    setSaving(true);
    setErr(null);
    try {
      for (const id of selectedIds) {
        const prod = productos?.find(p => p?.id === id);
        if (!prod) continue;
        const updates = {};

        if (fields?.familia_id?.enabled && fields?.familia_id?.value) updates.familia_id = fields?.familia_id?.value;
        if (fields?.proveedor_principal_id?.enabled && fields?.proveedor_principal_id?.value) updates.proveedor_principal_id = fields?.proveedor_principal_id?.value;
        if (fields?.tipo_iva?.enabled) updates.tipo_iva = parseFloat(fields?.tipo_iva?.value) || 10;
        if (fields?.recargo_equivalencia?.enabled) updates.recargo_equivalencia = parseFloat(fields?.recargo_equivalencia?.value) || RECARGO_DEFAULT;
        if (fields?.limpiar_margen?.enabled && fields?.limpiar_margen?.value) {
          updates.margen_objetivo = null;
        } else if (fields?.margen_objetivo?.enabled) {
          updates.margen_objetivo = parseFloat(fields?.margen_objetivo?.value) / 100;
        }
        if (fields?.activo?.enabled) updates.activo = fields?.activo?.value === 'true';
        if (fields?.bloqueo_precio?.enabled) updates.bloqueo_precio = fields?.bloqueo_precio?.value === 'true';

        // Aplicar PVP sugerido calculado
        if (fields?.aplicar_pvp_sugerido?.enabled && fields?.aplicar_pvp_sugerido?.value && !prod?.bloqueo_precio) {
          const coste = prod?.precio_compra;
          const margenDec = updates?.margen_objetivo ?? prod?.margen_objetivo ?? (MARGEN_DEFAULT / 100);
          const iva = updates?.tipo_iva ?? prod?.tipo_iva ?? 10;
          const regla = prod?.familia_regla_redondeo || 'TERMINAR_99';
          if (coste && coste > 0) {
            const pvpSug = calcularPvpSugerido(coste, margenDec, iva, regla);
            if (pvpSug) {
              updates.pvp_venta = parseFloat(pvpSug?.toFixed(2));
              updates.pvp_sugerido = parseFloat(pvpSug?.toFixed(2));
              const pvpSinIva = calcularPvpTeorico(coste, margenDec);
              if (pvpSinIva) {
                updates.pvp_sin_iva = parseFloat(pvpSinIva?.toFixed(4));
                updates.pvp_teorico = parseFloat(pvpSinIva?.toFixed(4));
              }
              const margenReal = calcularMargenReal(pvpSug, coste);
              if (margenReal !== null) updates.margen_real = parseFloat((margenReal / 100)?.toFixed(4));
            }
          }
        }

        if (Object.keys(updates)?.length > 0) {
          updates.updated_at = new Date()?.toISOString();
          await tiendaDb?.from('productos')?.update(updates)?.eq('id', id);
        }
      }
      onSave();
      onClose();
    } catch (e) { setErr(e?.message); setSaving(false); }
  };

  const famOpts = [{ value: '', label: 'Sin cambio' }, ...familias?.map(f => ({ value: f?.id, label: f?.nombre }))];
  const provOpts = [{ value: '', label: 'Sin cambio' }, ...proveedores?.map(p => ({ value: p?.id, label: p?.nombre }))];
  const activoOpts = [{ value: 'true', label: 'Activar' }, { value: 'false', label: 'Desactivar' }];
  const bloqueoOpts = [{ value: 'true', label: 'Bloquear PVP' }, { value: 'false', label: 'Desbloquear PVP' }];

  const FieldRow = ({ fieldKey, label, children }) => (
    <div className="flex items-center gap-3 py-2" style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
      <button type="button" onClick={() => toggle(fieldKey)} className="flex-shrink-0 w-5 h-5 rounded flex items-center justify-center transition-all" style={fields?.[fieldKey]?.enabled ? { background: `${GOLD}20`, border: `1px solid ${GOLD}40` } : { background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)' }}>
        {fields?.[fieldKey]?.enabled && <Icon name="Check" size={10} style={{ color: GOLD }} />}
      </button>
      <span className="font-oranienbaum text-white/60 text-xs w-44 flex-shrink-0">{label}</span>
      <div className="flex-1" style={{ opacity: fields?.[fieldKey]?.enabled ? 1 : 0.35, pointerEvents: fields?.[fieldKey]?.enabled ? 'auto' : 'none' }}>
        {children}
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.85)' }}>
      <div className="w-full max-w-xl rounded-2xl flex flex-col max-h-[90vh]" style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.10)' }}>
        <div className="flex items-center justify-between px-6 py-4 flex-shrink-0" style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${BLUE}15`, border: `1px solid ${BLUE}30` }}>
              <Icon name="Edit3" size={15} style={{ color: BLUE }} />
            </div>
            <div>
              <h3 className="font-oranienbaum font-bold text-white text-lg">Edición Masiva</h3>
              <p className="font-oranienbaum text-white/40 text-xs">{selectedIds?.length} productos seleccionados</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-white/40 hover:text-white/80"><Icon name="X" size={16} /></button>
        </div>

        <div className="overflow-y-auto flex-1 px-6 py-4">
          {err && <div className="mb-3 p-3 rounded-xl text-xs font-oranienbaum text-red-400" style={{ background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.20)' }}>{err}</div>}
          <p className="font-oranienbaum text-white/30 text-xs mb-3">Marca los campos que deseas modificar. Los campos no marcados no se modificarán.</p>

          <FieldRow fieldKey="familia_id" label="Familia">
            <CorporateSelect value={fields?.familia_id?.value} onChange={v => setVal('familia_id', v)} options={famOpts} searchable />
          </FieldRow>
          <FieldRow fieldKey="proveedor_principal_id" label="Proveedor">
            <CorporateSelect value={fields?.proveedor_principal_id?.value} onChange={v => setVal('proveedor_principal_id', v)} options={provOpts} searchable />
          </FieldRow>
          <FieldRow fieldKey="tipo_iva" label="IVA">
            <CorporateSelect value={fields?.tipo_iva?.value} onChange={v => setVal('tipo_iva', v)} options={IVA_OPTS} />
          </FieldRow>
          <FieldRow fieldKey="recargo_equivalencia" label="Recargo equiv. (%)">
            <input type="number" value={fields?.recargo_equivalencia?.value} onChange={e => setVal('recargo_equivalencia', e?.target?.value)} className={inputCls} style={inputSt} step="0.1" min="0" max="100" />
          </FieldRow>
          <FieldRow fieldKey="margen_objetivo" label="Margen específico (%)">
            <input type="number" value={fields?.margen_objetivo?.value} onChange={e => setVal('margen_objetivo', e?.target?.value)} className={inputCls} style={inputSt} step="0.1" min="0" max="100" />
          </FieldRow>
          <FieldRow fieldKey="limpiar_margen" label="Usar margen de familia">
            <button type="button" onClick={() => setVal('limpiar_margen', !fields?.limpiar_margen?.value)} className="flex items-center gap-2 px-3 py-1.5 rounded-xl text-xs font-oranienbaum transition-all" style={fields?.limpiar_margen?.value ? { background: 'rgba(52,211,153,0.12)', border: '1px solid rgba(52,211,153,0.25)', color: ACCENT } : { background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)', color: 'rgba(255,255,255,0.40)' }}>
              <Icon name={fields?.limpiar_margen?.value ? 'ToggleRight' : 'ToggleLeft'} size={14} />Eliminar margen específico
            </button>
          </FieldRow>
          <FieldRow fieldKey="aplicar_pvp_sugerido" label="Aplicar PVP sugerido">
            <button type="button" onClick={() => setVal('aplicar_pvp_sugerido', !fields?.aplicar_pvp_sugerido?.value)} className="flex items-center gap-2 px-3 py-1.5 rounded-xl text-xs font-oranienbaum transition-all" style={fields?.aplicar_pvp_sugerido?.value ? { background: `${GOLD}15`, border: `1px solid ${GOLD}30`, color: GOLD } : { background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)', color: 'rgba(255,255,255,0.40)' }}>
              <Icon name={fields?.aplicar_pvp_sugerido?.value ? 'ToggleRight' : 'ToggleLeft'} size={14} />Aplicar PVP sugerido (no bloqueados)
            </button>
          </FieldRow>
          <FieldRow fieldKey="activo" label="Estado">
            <CorporateSelect value={fields?.activo?.value} onChange={v => setVal('activo', v)} options={activoOpts} />
          </FieldRow>
          <FieldRow fieldKey="bloqueo_precio" label="Bloqueo PVP">
            <CorporateSelect value={fields?.bloqueo_precio?.value} onChange={v => setVal('bloqueo_precio', v)} options={bloqueoOpts} />
          </FieldRow>
        </div>

        <div className="px-6 py-4 flex-shrink-0" style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
          {confirmed && (
            <div className="mb-3 p-3 rounded-xl text-xs font-oranienbaum" style={{ background: 'rgba(251,191,36,0.08)', border: '1px solid rgba(251,191,36,0.20)', color: '#FBBF24' }}>
              ⚠️ Vas a modificar {selectedIds?.length} productos con {enabledCount} campo(s). ¿Confirmar?
            </div>
          )}
          <div className="flex gap-2 justify-end">
            <button onClick={onClose} className="px-4 py-2 rounded-xl text-sm font-oranienbaum text-white/40 hover:text-white/70" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}>Cancelar</button>
            <button onClick={handleApply} disabled={saving || enabledCount === 0} className="px-5 py-2 rounded-xl text-sm font-oranienbaum transition-all" style={{ background: `${BLUE}20`, border: `1px solid ${BLUE}40`, color: BLUE, opacity: enabledCount === 0 ? 0.4 : 1 }}>
              {saving ? 'Aplicando...' : confirmed ? `Confirmar (${selectedIds?.length} productos)` : `Aplicar a ${selectedIds?.length} productos`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── MODAL PRODUCTO ───────────────────────────────────────────────────────────
const ProductModal = ({ product, familias, marcas, proveedores, onSave, onClose }) => {
  const isEdit = !!product?.id;
  const [duplicados, setDuplicados] = useState([]);
  const [buscandoDups, setBuscandoDups] = useState(false);
  const [form, setForm] = useState({
    nombre: product?.nombre || '',
    codigo_interno: product?.codigo_interno || '',
    codigo_epelsa: product?.codigo_epelsa || '',
    ean: product?.ean || '',
    plu: product?.plu || '',
    descripcion: product?.descripcion || '',
    tipo_iva: product?.tipo_iva ?? 10,
    recargo_equivalencia: product?.recargo_equivalencia ?? RECARGO_DEFAULT,
    stock_minimo: product?.stock_minimo ?? 0,
    stock_maximo: product?.stock_maximo ?? '',
    punto_pedido: product?.punto_pedido ?? '',
    unidad_compra: product?.unidad_compra || 'UNIDAD',
    unidad_venta: product?.unidad_venta || 'UNIDAD',
    ubicacion: product?.ubicacion || '',
    familia_id: product?.familia_id || '',
    proveedor_principal_id: product?.proveedor_principal_id || '',
    activo: product?.activo ?? true,
    precio_compra: product?.precio_compra ?? '',
    margen_objetivo: product?.margen_objetivo != null ? (product?.margen_objetivo * 100)?.toFixed(1) : '',
    pvp_venta: product?.pvp_venta ?? '',
    bloqueo_precio: product?.bloqueo_precio ?? false,
    actualizar_pvp_automatico: product?.actualizar_pvp_automatico ?? true,
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const set = (key, val) => setForm(p => ({ ...p, [key]: val }));

  // Detección de duplicados (solo en modo creación)
  useEffect(() => {
    if (isEdit) return;
    const t = setTimeout(async () => {
      if (!form?.ean && !form?.plu && !form?.codigo_interno && !form?.referencia_proveedor) return;
      setBuscandoDups(true);
      try {
        const dups = await buscarDuplicados({
          codigoInterno: form?.codigo_interno,
          plu: form?.plu,
          ean: form?.ean,
          referenciaProveedor: form?.referencia_proveedor,
          excludeId: product?.id,
        });
        setDuplicados(dups);
      } catch (e) { /* silencioso */ }
      setBuscandoDups(false);
    }, 700);
    return () => clearTimeout(t);
  }, [form?.ean, form?.plu, form?.codigo_interno, form?.referencia_proveedor, isEdit, product?.id]);

  // Cálculos reactivos
  const coste = parseFloat(form?.precio_compra) || null;
  const iva = parseFloat(form?.tipo_iva) || 10;
  const recargo = parseFloat(form?.recargo_equivalencia) || RECARGO_DEFAULT;
  const margenProducto = form?.margen_objetivo !== '' ? parseFloat(form?.margen_objetivo) / 100 : null;
  const familiaSeleccionada = familias?.find(f => f?.id === form?.familia_id);
  const margenFamilia = familiaSeleccionada?.margen_objetivo ?? null;
  const margenAplicado = margenProducto ?? margenFamilia ?? (MARGEN_DEFAULT / 100);
  const origenMargen = margenProducto != null ? 'Producto' : margenFamilia != null ? `Familia (${familiaSeleccionada?.nombre})` : 'General tienda (35%)';
  const reglaRedondeo = familiaSeleccionada?.regla_redondeo || 'TERMINAR_99';

  const costeConIva = coste != null ? calcularCosteConIva(coste, iva) : null;
  const costeConImpuestos = coste != null ? calcularCosteConImpuestos(coste, iva, recargo) : null;
  const pvpTeorico = coste != null ? calcularPvpTeorico(coste, margenAplicado) : null;
  const pvpSugerido = coste != null ? calcularPvpSugerido(coste, margenAplicado, iva, reglaRedondeo) : null;
  const pvpActualNum = parseFloat(form?.pvp_venta) || null;
  const margenRealDisplay = pvpActualNum && coste ? calcularMargenReal(pvpActualNum, coste) : null;

  const handleSave = async () => {
    if (!form?.nombre?.trim()) { setErr('El nombre es obligatorio'); return; }
    try {
      setSaving(true);
      setErr(null);
      const margenDec = form?.margen_objetivo !== '' ? parseFloat(form?.margen_objetivo) / 100 : null;
      const pvpActual = form?.pvp_venta !== '' ? parseFloat(form?.pvp_venta) : null;
      const margenReal = pvpActual && coste ? calcularMargenReal(pvpActual, coste) : null;

      const payload = {
        nombre: form?.nombre?.trim(),
        codigo_interno: form?.codigo_interno || null,
        codigo_epelsa: form?.codigo_epelsa || null,
        ean: form?.ean || null,
        plu: form?.plu || null,
        descripcion: form?.descripcion || null,
        tipo_iva: iva,
        recargo_equivalencia: recargo,
        stock_minimo: parseFloat(form?.stock_minimo) || 0,
        stock_maximo: form?.stock_maximo !== '' ? parseFloat(form?.stock_maximo) : null,
        punto_pedido: form?.punto_pedido !== '' ? parseFloat(form?.punto_pedido) : null,
        unidad_compra: form?.unidad_compra || 'UNIDAD',
        unidad_venta: form?.unidad_venta || 'UNIDAD',
        ubicacion: form?.ubicacion || null,
        familia_id: form?.familia_id || null,
        proveedor_principal_id: form?.proveedor_principal_id || null,
        activo: form?.activo ?? true,
        precio_compra: coste,
        precio_compra_con_iva: costeConIva,
        coste_con_impuestos: costeConImpuestos,
        margen_objetivo: margenDec,
        bloqueo_precio: form?.bloqueo_precio ?? false,
        actualizar_pvp_automatico: form?.actualizar_pvp_automatico ?? true,
        pvp_venta: pvpActual,
        pvp_sin_iva: pvpTeorico ? parseFloat(pvpTeorico?.toFixed(4)) : null,
        pvp_teorico: pvpTeorico ? parseFloat(pvpTeorico?.toFixed(4)) : null,
        pvp_sugerido: pvpSugerido ? parseFloat(pvpSugerido?.toFixed(2)) : null,
        margen_real: margenReal != null ? parseFloat((margenReal / 100)?.toFixed(4)) : null,
        // Nuevo producto → PENDIENTE_ALTA; edición → PENDIENTE_ACTUALIZACION si cambia campo relevante
        ...(!isEdit ? { epelsa_sync_status: 'PENDIENTE_ALTA' } : (
          isEdit && (
            form?.nombre !== product?.nombre ||
            pvpActual !== product?.pvp_venta ||
            iva !== product?.tipo_iva ||
            form?.unidad_venta !== product?.unidad_venta ||
            form?.activo !== product?.activo ||
            form?.plu !== product?.plu ||
            form?.ean !== product?.ean
          ) ? { epelsa_sync_status: 'PENDIENTE_ACTUALIZACION' } : {}
        )),
      };

      // Si cambia el coste, guardar histórico
      await productosService?.guardarHistoricoCoste(product?.id, {
        precio_sin_iva: coste,
        iva,
        recargo,
        coste_con_impuestos: costeConImpuestos,
        pvp_sugerido: pvpSugerido,
        pvp_actual: pvpActual,
        margen_aplicado: margenAplicado,
        proveedor_id: form?.proveedor_principal_id || null,
      });

      if (isEdit) {
        await tiendaDb?.from('productos')?.update({ ...payload, updated_at: new Date()?.toISOString() })?.eq('id', product?.id);
      } else {
        await tiendaDb?.from('productos')?.insert(payload);
      }
      onSave();
      onClose();
    } catch (e) { setErr(e?.message || 'Error al guardar'); }
    finally { setSaving(false); }
  };

  const SectionTitle = ({ title }) => <p className="font-oranienbaum text-white/30 text-[10px] uppercase tracking-wider mb-2 mt-3">{title}</p>;
  const toggleBtn = (key, label) => (
    <button type="button" onClick={() => set(key, !form?.[key])} className="flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-oranienbaum transition-all" style={form?.[key] ? { background: 'rgba(52,211,153,0.12)', border: '1px solid rgba(52,211,153,0.25)', color: ACCENT } : { background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)', color: 'rgba(255,255,255,0.40)' }}>
      <Icon name={form?.[key] ? 'ToggleRight' : 'ToggleLeft'} size={16} />{label}
    </button>
  );

  const famOpts = [{ value: '', label: '— Sin familia —' }, ...familias?.map(f => ({ value: f?.id, label: f?.nombre }))];
  const provOpts = [{ value: '', label: '— Sin proveedor —' }, ...proveedores?.map(p => ({ value: p?.id, label: p?.nombre }))];
  const unidadOpts = ['UNIDAD', 'KG', 'GRAMO', 'LITRO', 'CAJA', 'PAQUETE', 'PIEZA']?.map(u => ({ value: u, label: u }));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.80)' }}>
      <div className="w-full max-w-2xl rounded-2xl flex flex-col max-h-[92vh]" style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.10)' }}>
        <div className="flex items-center justify-between px-6 py-4 flex-shrink-0" style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${GOLD}15`, border: `1px solid ${GOLD}30` }}>
              <Icon name="Box" size={15} style={{ color: GOLD }} />
            </div>
            <h3 className="font-oranienbaum font-bold text-white text-lg">{isEdit ? 'Editar Producto' : 'Nuevo Producto'}</h3>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-white/40 hover:text-white/80"><Icon name="X" size={16} /></button>
        </div>
        <div className="overflow-y-auto flex-1 px-6 py-4 space-y-2">
          {err && <div className="p-3 rounded-xl text-xs font-oranienbaum text-red-400" style={{ background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.20)' }}>{err}</div>}

          {/* Alerta duplicados */}
          {!isEdit && duplicados?.length > 0 && (
            <div className="p-3 rounded-xl" style={{ background: 'rgba(251,146,60,0.08)', border: '1px solid rgba(251,146,60,0.25)' }}>
              <div className="flex items-center gap-2 mb-2">
                <Icon name="AlertTriangle" size={14} style={{ color: ORANGE }} />
                <p className="font-oranienbaum text-sm font-bold" style={{ color: ORANGE }}>Este artículo podría existir ya</p>
              </div>
              {duplicados?.map(d => (
                <div key={d?.id} className="flex items-center justify-between py-1.5" style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
                  <div>
                    <p className="font-oranienbaum text-white/80 text-xs">{d?.nombre}</p>
                    <p className="font-oranienbaum text-white/40 text-[10px]">{d?.motivo}</p>
                  </div>
                  <span className="font-oranienbaum text-[10px] px-2 py-0.5 rounded-full" style={{ background: `${ORANGE}15`, color: ORANGE, border: `1px solid ${ORANGE}30` }}>
                    {d?.codigo_interno || d?.ean || d?.plu || '—'}
                  </span>
                </div>
              ))}
              <p className="font-oranienbaum text-white/40 text-[10px] mt-2">Verifica antes de crear para evitar duplicados.</p>
            </div>
          )}

          <SectionTitle title="Datos Generales" />
          <div>
            <label className="block font-oranienbaum text-white/50 text-xs mb-1">Nombre *</label>
            <input type="text" value={form?.nombre} onChange={e => set('nombre', e?.target?.value)} className={inputCls} style={inputSt} placeholder="Nombre del producto" />
          </div>
          <div className="grid grid-cols-4 gap-3">
            {[{ label: 'Código Interno', key: 'codigo_interno' }, { label: 'Código Epelsa', key: 'codigo_epelsa' }, { label: 'EAN / Barras', key: 'ean' }, { label: 'PLU', key: 'plu' }]?.map(f => (
              <div key={f?.key}>
                <label className="block font-oranienbaum text-white/50 text-xs mb-1">{f?.label}</label>
                <input type="text" value={form?.[f?.key]} onChange={e => set(f?.key, e?.target?.value)} className={inputCls} style={inputSt} />
              </div>
            ))}
          </div>

          <SectionTitle title="Clasificación" />
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block font-oranienbaum text-white/50 text-xs mb-1">Familia</label>
              <CorporateSelect value={form?.familia_id} onChange={v => set('familia_id', v)} options={famOpts} searchable />
            </div>
            <div>
              <label className="block font-oranienbaum text-white/50 text-xs mb-1">Proveedor principal</label>
              <CorporateSelect value={form?.proveedor_principal_id} onChange={v => set('proveedor_principal_id', v)} options={provOpts} searchable />
            </div>
          </div>

          <SectionTitle title="Unidades" />
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block font-oranienbaum text-white/50 text-xs mb-1">Unidad compra</label>
              <CorporateSelect value={form?.unidad_compra} onChange={v => set('unidad_compra', v)} options={unidadOpts} />
            </div>
            <div>
              <label className="block font-oranienbaum text-white/50 text-xs mb-1">Unidad venta</label>
              <CorporateSelect value={form?.unidad_venta} onChange={v => set('unidad_venta', v)} options={unidadOpts} />
            </div>
            <div>
              <label className="block font-oranienbaum text-white/50 text-xs mb-1">Ubicación</label>
              <input type="text" value={form?.ubicacion} onChange={e => set('ubicacion', e?.target?.value)} className={inputCls} style={inputSt} placeholder="Ej: Estante A-3" />
            </div>
          </div>

          <SectionTitle title="Impuestos" />
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block font-oranienbaum text-white/50 text-xs mb-1">IVA (%)</label>
              <CorporateSelect value={String(form?.tipo_iva)} onChange={v => set('tipo_iva', parseFloat(v))} options={IVA_OPTS} />
            </div>
            <div>
              <label className="block font-oranienbaum text-white/50 text-xs mb-1">Recargo equivalencia (%) <span style={{ color: GOLD }}>def. {RECARGO_DEFAULT}</span></label>
              <input type="number" value={form?.recargo_equivalencia} onChange={e => set('recargo_equivalencia', e?.target?.value)} className={inputCls} style={inputSt} step="0.1" min="0" max="100" />
            </div>
          </div>

          <SectionTitle title="Precios" />
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block font-oranienbaum text-white/50 text-xs mb-1">Coste sin IVA (€)</label>
              <input type="number" value={form?.precio_compra} onChange={e => set('precio_compra', e?.target?.value)} className={inputCls} style={inputSt} step="0.01" min="0" placeholder="0,00" />
            </div>
            <div>
              <label className="block font-oranienbaum text-white/50 text-xs mb-1">Margen específico (%) <span style={{ color: GOLD }}>def. {MARGEN_DEFAULT}</span></label>
              <input type="number" value={form?.margen_objetivo} onChange={e => set('margen_objetivo', e?.target?.value)} className={inputCls} style={inputSt} step="0.1" min="0" max="99" placeholder={`${MARGEN_DEFAULT}`} />
            </div>
          </div>
          {/* Precios calculados */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 p-3 rounded-xl" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
            {[
              { label: 'Coste c/IVA', value: fmtEur(costeConIva), color: BLUE },
              { label: 'Coste c/Impuestos', value: fmtEur(costeConImpuestos), color: PURPLE },
              { label: 'PVP teórico s/IVA', value: fmtEur(pvpTeorico), color: GOLD },
              { label: 'PVP sugerido c/IVA', value: fmtEur(pvpSugerido), color: ACCENT },
            ]?.map(item => (
              <div key={item?.label} className="text-center">
                <p className="font-oranienbaum text-xs font-bold" style={{ color: item?.color }}>{item?.value}</p>
                <p className="font-oranienbaum text-white/30 text-[10px]">{item?.label}</p>
              </div>
            ))}
            <div className="col-span-2 text-center">
              <p className="font-oranienbaum text-xs" style={{ color: GOLD }}>Margen: {origenMargen} · Redondeo: {REGLA_OPTS?.find(r => r?.value === reglaRedondeo)?.label}</p>
              <p className="font-oranienbaum text-white/30 text-[10px]">Fórmula: coste / (1 - margen)</p>
            </div>
          </div>

          <SectionTitle title="PVP Actual (precio vigente en tienda)" />
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block font-oranienbaum text-white/50 text-xs mb-1">PVP actual (€) <span style={{ color: BLUE }}>precio real en tienda</span></label>
              <input type="number" value={form?.pvp_venta} onChange={e => set('pvp_venta', e?.target?.value)} className={inputCls} style={inputSt} step="0.01" min="0" placeholder="0,00" disabled={form?.bloqueo_precio} />
            </div>
            <div className="flex flex-col justify-end">
              <p className="font-oranienbaum text-white/30 text-xs mb-1">Margen real sobre PVP actual</p>
              <p className="font-oranienbaum text-sm font-bold" style={{ color: margenRealDisplay != null ? (margenRealDisplay < 0 ? RED : ACCENT) : 'rgba(255,255,255,0.30)' }}>
                {margenRealDisplay != null ? fmtPct(margenRealDisplay) : '—'}
              </p>
              {pvpSugerido && pvpActualNum && Math.abs(pvpSugerido - pvpActualNum) > 0.01 && !form?.bloqueo_precio && (
                <button type="button" onClick={() => set('pvp_venta', pvpSugerido?.toFixed(2))} className="mt-1 text-xs font-oranienbaum px-2 py-1 rounded-lg transition-all" style={{ background: `${GOLD}15`, border: `1px solid ${GOLD}30`, color: GOLD }}>
                  Aplicar PVP sugerido ({fmtEur(pvpSugerido)})
                </button>
              )}
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            {toggleBtn('bloqueo_precio', 'PVP Bloqueado')}
            {toggleBtn('actualizar_pvp_automatico', 'Actualizar PVP auto')}
            {toggleBtn('activo', 'Activo')}
          </div>
          {form?.bloqueo_precio && (
            <div className="p-2 rounded-xl text-xs font-oranienbaum" style={{ background: `${BLUE}10`, border: `1px solid ${BLUE}20`, color: BLUE }}>
              🔒 PVP bloqueado: se recalcula PVP sugerido pero no se modifica PVP actual
            </div>
          )}

          <SectionTitle title="Inventario" />
          <div className="grid grid-cols-3 gap-3">
            {[{ label: 'Stock mínimo', key: 'stock_minimo' }, { label: 'Stock máximo', key: 'stock_maximo' }, { label: 'Punto de pedido', key: 'punto_pedido' }]?.map(f => (
              <div key={f?.key}>
                <label className="block font-oranienbaum text-white/50 text-xs mb-1">{f?.label}</label>
                <input type="number" value={form?.[f?.key]} onChange={e => set(f?.key, e?.target?.value)} className={inputCls} style={inputSt} min="0" step="0.01" />
              </div>
            ))}
          </div>
        </div>
        <div className="px-6 py-4 flex-shrink-0 flex justify-end gap-2" style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
          <button onClick={onClose} className="px-4 py-2 rounded-xl text-sm font-oranienbaum text-white/40 hover:text-white/70" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}>Cancelar</button>
          <button onClick={handleSave} disabled={saving} className="px-5 py-2 rounded-xl text-sm font-oranienbaum transition-all" style={{ background: `${GOLD}20`, border: `1px solid ${GOLD}40`, color: GOLD }}>
            {saving ? 'Guardando...' : isEdit ? 'Actualizar' : 'Crear producto'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── PÁGINA PRINCIPAL ─────────────────────────────────────────────────────────
const TiendaProductos = () => {
  const [productos, setProductos] = useState([]);
  const [familias, setFamilias] = useState([]);
  const [marcas, setMarcas] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [filterFamilia, setFilterFamilia] = useState('');
  const [filterProveedor, setFilterProveedor] = useState('');
  const [filterExtra, setFilterExtra] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [modal, setModal] = useState(null);
  const [editProduct, setEditProduct] = useState(null);
  const [historicoProduct, setHistoricoProduct] = useState(null);
  const [sincronizandoId, setSincronizandoId] = useState(null);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [prods, fams, mks, provs] = await Promise.all([
        productosService?.getAll(),
        tiendaDb?.from('familias_producto')?.select('*')?.order('nombre'),
        marcasService?.getAll(),
        proveedoresService?.getAll({ includeInactive: false }),
      ]);
      setProductos(prods || []);
      setFamilias(fams?.data || []);
      setMarcas(mks || []);
      setProveedores(provs || []);
    } catch (e) { setError(e?.message); }
    setLoading(false);
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  // ─── KPIs DASHBOARD ─────────────────────────────────────────────────────────
  const kpis = useMemo(() => {
    const total = productos?.length;
    let sinCoste = 0, sinProveedor = 0, stockNegativo = 0, pvpBajoCoste = 0, margenInferior = 0, preciosBloqueados = 0, pendienteEpelsa = 0, errorEpelsa = 0;
    for (const p of productos) {
      const coste = p?.precio_compra;
      const pvp = p?.pvp_venta;
      const stock = p?.stock_actual ?? p?.stock_disponible ?? 0;
      const margenObj = getMargenEfectivo(p);
      if (!coste || coste <= 0) sinCoste++;
      if (!p?.proveedor_principal_id) sinProveedor++;
      if (stock < 0) stockNegativo++;
      if (coste && pvp && pvp < coste) pvpBajoCoste++;
      if (coste && pvp && margenObj) {
        const mr = calcularMargenReal(pvp, coste);
        if (mr !== null && mr < (margenObj * 100) * 0.85) margenInferior++;
      }
      if (p?.bloqueo_precio) preciosBloqueados++;
      if (p?.epelsa_sync_status === 'PENDIENTE_EPELSA' || p?.epelsa_sync_status === 'PENDIENTE_ALTA' || p?.epelsa_sync_status === 'PENDIENTE_ACTUALIZACION') pendienteEpelsa++;
      if (p?.epelsa_sync_status === 'ERROR') errorEpelsa++;
    }
    return { total, sinCoste, sinProveedor, stockNegativo, pvpBajoCoste, margenInferior, preciosBloqueados, pendienteEpelsa, errorEpelsa };
  }, [productos]);

  // ─── FILTROS ─────────────────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    return productos?.filter(p => {
      if (search) {
        const s = search?.toLowerCase();
        const matches = p?.nombre?.toLowerCase()?.includes(s) ||
          p?.codigo_interno?.toLowerCase()?.includes(s) ||
          p?.ean?.toLowerCase()?.includes(s) ||
          p?.plu?.toLowerCase()?.includes(s) ||
          p?.codigo_epelsa?.toLowerCase()?.includes(s);
        if (!matches) return false;
      }
      if (filterFamilia && p?.familia_id !== filterFamilia) return false;
      if (filterProveedor && p?.proveedor_principal_id !== filterProveedor) return false;

      const coste = p?.precio_compra;
      const pvp = p?.pvp_venta;
      const stock = p?.stock_actual ?? p?.stock_disponible ?? 0;
      const margenObj = getMargenEfectivo(p);

      switch (filterExtra) {
        case 'sin_coste': if (coste && coste > 0) return false; break;
        case 'sin_proveedor': if (p?.proveedor_principal_id) return false; break;
        case 'sin_familia': if (p?.familia_id) return false; break;
        case 'sin_ean': if (p?.ean) return false; break;
        case 'stock_negativo': if (stock >= 0) return false; break;
        case 'pvp_bajo_coste': if (!coste || !pvp || pvp >= coste) return false; break;
        case 'margen_inferior': {
          if (!coste || !pvp || !margenObj) return false;
          const mr = calcularMargenReal(pvp, coste);
          if (mr === null || mr >= (margenObj * 100) * 0.85) return false;
          break;
        }
        case 'precio_bloqueado': if (!p?.bloqueo_precio) return false; break;
        case 'activos': if (!p?.activo) return false; break;
        case 'inactivos': if (p?.activo) return false; break;
        case 'pendiente_alta_epelsa': if (p?.epelsa_sync_status !== 'PENDIENTE_ALTA') return false; break;
        case 'pendiente_actualizacion_epelsa': if (p?.epelsa_sync_status !== 'PENDIENTE_ACTUALIZACION') return false; break;
        case 'pendiente_epelsa': if (p?.epelsa_sync_status !== 'PENDIENTE_EPELSA' && p?.epelsa_sync_status !== 'PENDIENTE_ALTA' && p?.epelsa_sync_status !== 'PENDIENTE_ACTUALIZACION') return false; break;
        case 'sincronizado_epelsa': if (p?.epelsa_sync_status !== 'SINCRONIZADO') return false; break;
        case 'error_epelsa': if (p?.epelsa_sync_status !== 'ERROR') return false; break;
        default: break;
      }
      return true;
    });
  }, [productos, search, filterFamilia, filterProveedor, filterExtra]);

  const toggleSelect = (id) => setSelected(prev => { const n = new Set(prev); n?.has(id) ? n?.delete(id) : n?.add(id); return n; });
  const toggleAll = () => setSelected(prev => prev?.size === filtered?.length ? new Set() : new Set(filtered.map(p => p.id)));

  const handleToggleActivo = async (p) => {
    await tiendaDb?.from('productos')?.update({ activo: !p?.activo, updated_at: new Date()?.toISOString() })?.eq('id', p?.id);
    await loadAll();
  };

  // Marcar producto como "intentando sincronizar" — encola en cola_sync_productos_epelsa
  // NO marca como SINCRONIZADO hasta recibir confirmación real de Epelsa
  const handleSincronizarEpelsa = async (p) => {
    setSincronizandoId(p?.id);
    try {
      // Insertar en cola de sincronización (o actualizar si ya existe entrada pendiente)
      const tipoOp = p?.epelsa_sync_status === 'PENDIENTE_ALTA' ? 'ALTA' : 'ACTUALIZACION';
      await tiendaDb?.from('cola_sync_productos_epelsa')?.insert({
        producto_id: p?.id,
        codigo_epelsa: p?.codigo_epelsa || null,
        plu: p?.plu || null,
        ean: p?.ean || null,
        nombre: p?.nombre || null,
        pvp: p?.pvp_venta || null,
        tipo_iva: p?.tipo_iva || null,
        unidad_venta: p?.unidad_venta || null,
        activo: p?.activo ?? true,
        tipo_operacion: tipoOp,
        estado: 'PENDIENTE',
        origen: 'MANUAL',
      });
      // Nota: NO cambiamos epelsa_sync_status a SINCRONIZADO aquí.
      // El estado solo cambia a SINCRONIZADO cuando Epelsa confirme el alta real.
      // Por ahora el producto queda en PENDIENTE_ALTA/PENDIENTE_ACTUALIZACION.
      alert(`Producto encolado para sincronización con Epelsa (${tipoOp}). Estado: PENDIENTE.\n\nNota: El estado cambiará a SINCRONIZADO únicamente cuando la integración real con Epelsa confirme el alta.`);
    } catch (e) {
      alert(`Error al encolar: ${e?.message}`);
    } finally {
      setSincronizandoId(null);
    }
  };

  const famOpts = [{ value: '', label: 'Todas las familias' }, ...familias?.map(f => ({ value: f?.id, label: f?.nombre }))];
  const provOpts = [{ value: '', label: 'Todos los proveedores' }, ...proveedores?.map(p => ({ value: p?.id, label: p?.nombre }))];
  const extraOpts = [
    { value: '', label: 'Todos los productos' },
    { value: 'activos', label: 'Activos' },
    { value: 'inactivos', label: 'Inactivos' },
    { value: 'pendiente_alta_epelsa', label: '🟡 Pendiente alta Epelsa' },
    { value: 'pendiente_actualizacion_epelsa', label: '🟠 Pendiente actualización Epelsa' },
    { value: 'pendiente_epelsa', label: '🟡 Pendiente Epelsa (todos)' },
    { value: 'sincronizado_epelsa', label: '🟢 Sincronizados Epelsa' },
    { value: 'error_epelsa', label: '🔴 Error Epelsa' },
    { value: 'sin_coste', label: '⚠ Sin coste' },
    { value: 'sin_proveedor', label: '⚠ Sin proveedor' },
    { value: 'sin_familia', label: '⚠ Sin familia' },
    { value: 'sin_ean', label: '⚠ Sin EAN' },
    { value: 'stock_negativo', label: '🔴 Stock negativo' },
    { value: 'pvp_bajo_coste', label: '🔴 PVP bajo coste' },
    { value: 'margen_inferior', label: '🟠 Margen inferior objetivo' },
    { value: 'precio_bloqueado', label: '🔵 Precio bloqueado' },
  ];

  const getMargenColor = (margenReal, margenObj) => {
    if (margenReal == null) return 'rgba(255,255,255,0.30)';
    if (margenObj && margenReal < (margenObj * 100) * 0.85) return RED;
    if (margenObj && margenReal < (margenObj * 100) * 0.95) return ORANGE;
    return ACCENT;
  };

  const KpiCard = ({ label, value, color, filter }) => (
    <button
      onClick={() => setFilterExtra(filterExtra === filter ? '' : filter)}
      className="flex flex-col items-center justify-center p-3 rounded-xl transition-all text-center"
      style={{
        background: filterExtra === filter ? `${color}15` : 'rgba(255,255,255,0.02)',
        border: `1px solid ${filterExtra === filter ? color + '40' : 'rgba(255,255,255,0.07)'}`,
        minWidth: '90px',
      }}
    >
      <span className="font-oranienbaum font-bold text-xl" style={{ color }}>{value}</span>
      <span className="font-oranienbaum text-white/40 text-[10px] leading-tight mt-0.5">{label}</span>
    </button>
  );

  return (
    <div className="min-h-screen px-4 py-6 md:px-8" style={{ background: 'linear-gradient(135deg, #0A0A0A 0%, #111111 50%, #0D0D0D 100%)' }}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-5">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: `${GOLD}15`, border: `1px solid ${GOLD}30` }}>
              <Icon name="Box" size={18} style={{ color: GOLD }} />
            </div>
            <h1 className="font-oranienbaum font-bold text-white text-2xl md:text-3xl tracking-wide">Productos</h1>
          </div>
          <p className="font-oranienbaum text-white/40 text-sm ml-12">{kpis?.total} productos · Margen sobre PVP · Recargo 1,4%</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={() => setModal('familias')} className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-oranienbaum transition-all" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)', color: 'rgba(255,255,255,0.60)' }}>
            <Icon name="Tag" size={13} />Familias
          </button>
          <button onClick={loadAll} className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-oranienbaum transition-all" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)', color: 'rgba(255,255,255,0.60)' }}>
            <Icon name="RefreshCw" size={13} />Actualizar
          </button>
          <button onClick={() => { setEditProduct(null); setModal('new'); }} className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-oranienbaum transition-all" style={{ background: `${GOLD}20`, border: `1px solid ${GOLD}40`, color: GOLD }}>
            <Icon name="Plus" size={14} />Nuevo producto
          </button>
        </div>
      </div>

      {/* Dashboard KPIs */}
      <div className="flex flex-wrap gap-2 mb-5">
        <KpiCard label="Total" value={kpis?.total} color="rgba(255,255,255,0.60)" filter="" />
        <KpiCard label="Sin coste" value={kpis?.sinCoste} color={kpis?.sinCoste > 0 ? RED : ACCENT} filter="sin_coste" />
        <KpiCard label="Sin proveedor" value={kpis?.sinProveedor} color={kpis?.sinProveedor > 0 ? ORANGE : ACCENT} filter="sin_proveedor" />
        <KpiCard label="Stock negativo" value={kpis?.stockNegativo} color={kpis?.stockNegativo > 0 ? RED : ACCENT} filter="stock_negativo" />
        <KpiCard label="PVP bajo coste" value={kpis?.pvpBajoCoste} color={kpis?.pvpBajoCoste > 0 ? RED : ACCENT} filter="pvp_bajo_coste" />
        <KpiCard label="Margen inferior" value={kpis?.margenInferior} color={kpis?.margenInferior > 0 ? ORANGE : ACCENT} filter="margen_inferior" />
        <KpiCard label="Precio bloqueado" value={kpis?.preciosBloqueados} color={BLUE} filter="precio_bloqueado" />
        <KpiCard label="Pendiente Epelsa" value={kpis?.pendienteEpelsa} color={kpis?.pendienteEpelsa > 0 ? GOLD : ACCENT} filter="pendiente_epelsa" />
        {kpis?.errorEpelsa > 0 && <KpiCard label="Error Epelsa" value={kpis?.errorEpelsa} color={RED} filter="error_epelsa" />}
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap gap-2 mb-4">
        <div className="flex items-center gap-2 flex-1 min-w-48 px-3 rounded-xl" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', height: '38px' }}>
          <Icon name="Search" size={14} className="text-white/30 flex-shrink-0" />
          <input type="text" value={search} onChange={e => setSearch(e?.target?.value)} placeholder="Buscar por nombre, código, EAN, Epelsa..." className="flex-1 bg-transparent outline-none font-oranienbaum text-white/80 text-sm" />
        </div>
        <div className="w-44"><CorporateSelect value={filterFamilia} onChange={setFilterFamilia} options={famOpts} searchable /></div>
        <div className="w-44"><CorporateSelect value={filterProveedor} onChange={setFilterProveedor} options={provOpts} searchable /></div>
        <div className="w-48"><CorporateSelect value={filterExtra} onChange={setFilterExtra} options={extraOpts} /></div>
      </div>

      {/* Barra acciones masivas */}
      {selected?.size > 0 && (
        <div className="flex items-center gap-3 mb-4 px-4 py-3 rounded-xl" style={{ background: `${BLUE}10`, border: `1px solid ${BLUE}25` }}>
          <span className="font-oranienbaum text-sm" style={{ color: BLUE }}>{selected?.size} seleccionados</span>
          <button onClick={() => setModal('bulk')} className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-oranienbaum transition-all" style={{ background: `${BLUE}20`, border: `1px solid ${BLUE}40`, color: BLUE }}>
            <Icon name="Edit3" size={12} />Edición masiva
          </button>
          <button onClick={() => setSelected(new Set())} className="ml-auto p-1.5 rounded-lg text-white/30 hover:text-white/60"><Icon name="X" size={14} /></button>
        </div>
      )}

      {error && <div className="mb-4 p-4 rounded-xl flex items-center gap-3" style={{ background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.20)' }}><Icon name="AlertCircle" size={16} className="text-red-400 flex-shrink-0" /><p className="font-oranienbaum text-red-400 text-sm">{error}</p></div>}

      {/* Tabla */}
      <div className="rounded-2xl overflow-hidden" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)' }}>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.06)', background: 'rgba(255,255,255,0.02)' }}>
                <th className="px-3 py-3 text-left w-8">
                  <button onClick={toggleAll} className="w-4 h-4 rounded flex items-center justify-center" style={{ background: selected?.size === filtered?.length && filtered?.length > 0 ? `${GOLD}20` : 'rgba(255,255,255,0.05)', border: `1px solid ${selected?.size === filtered?.length && filtered?.length > 0 ? GOLD : 'rgba(255,255,255,0.15)'}` }}>
                    {selected?.size === filtered?.length && filtered?.length > 0 && <Icon name="Check" size={9} style={{ color: GOLD }} />}
                  </button>
                </th>
                {['Código', 'Nombre / Estado', 'Familia', 'Proveedor', 'Coste s/IVA', 'Coste c/IVA', 'Coste c/Imp.', 'Margen real', 'Margen obj.', 'PVP actual', 'PVP sugerido', 'Epelsa', 'Stock', 'Acciones']?.map(h => (
                  <th key={h} className="px-3 py-3 text-left font-oranienbaum text-white/30 text-xs uppercase tracking-wider whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={15} className="px-4 py-12 text-center"><div className="flex items-center justify-center gap-3"><div className="w-5 h-5 rounded-full border-2 border-white/10 border-t-white/60 animate-spin" /><span className="font-oranienbaum text-white/40 text-sm">Cargando productos...</span></div></td></tr>
              ) : filtered?.length === 0 ? (
                <tr><td colSpan={15} className="px-4 py-12 text-center"><Icon name="Box" size={32} className="text-white/10 mx-auto mb-2" /><p className="font-oranienbaum text-white/30 text-sm">No se encontraron productos</p></td></tr>
              ) : filtered?.map(p => {
                const coste = p?.precio_compra;
                const pvpActual = p?.pvp_venta;
                const stock = p?.stock_actual ?? p?.stock_disponible ?? 0;
                const costeConIva = coste ? calcularCosteConIva(coste, p?.tipo_iva ?? 10) : null;
                const costeConImp = coste ? calcularCosteConImpuestos(coste, p?.tipo_iva ?? 10, p?.recargo_equivalencia ?? RECARGO_DEFAULT) : null;
                const margenReal = pvpActual && coste ? calcularMargenReal(pvpActual, coste) : null;
                const margenObj = getMargenEfectivo(p);
                const pvpSug = p?.pvp_sugerido || getPvpSugeridoCalc(p);
                const estados = calcularEstadosProducto({ ...p, stock_actual: stock });

                return (
                  <tr key={p?.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }} className="hover:bg-white/[0.02] transition-colors">
                    <td className="px-3 py-2.5">
                      <button onClick={() => toggleSelect(p?.id)} className="w-4 h-4 rounded flex items-center justify-center" style={{ background: selected?.has(p?.id) ? `${GOLD}20` : 'rgba(255,255,255,0.05)', border: `1px solid ${selected?.has(p?.id) ? GOLD : 'rgba(255,255,255,0.15)'}` }}>
                        {selected?.has(p?.id) && <Icon name="Check" size={9} style={{ color: GOLD }} />}
                      </button>
                    </td>
                    <td className="px-3 py-2.5">
                      <span className="font-oranienbaum text-white/50 text-xs">{p?.codigo_interno || p?.codigo_epelsa || '—'}</span>
                    </td>
                    <td className="px-3 py-2.5 max-w-[180px]">
                      <p className="font-oranienbaum text-white/85 text-sm truncate">{p?.nombre}</p>
                      <EstadoBadge estados={estados} />
                    </td>
                    <td className="px-3 py-2.5">
                      {p?.familia_nombre ? (
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-oranienbaum" style={{ background: `${GOLD}10`, color: GOLD, border: `1px solid ${GOLD}20` }}>{p?.familia_nombre}</span>
                      ) : (
                        <span className="font-oranienbaum text-white/20 text-xs">Sin familia</span>
                      )}
                    </td>
                    <td className="px-3 py-2.5"><span className="font-oranienbaum text-white/60 text-xs">{p?.proveedor_nombre || '—'}</span></td>
                    <td className="px-3 py-2.5"><span className="font-oranienbaum text-white/80 text-xs">{fmtEur(coste)}</span></td>
                    <td className="px-3 py-2.5"><span className="font-oranienbaum text-xs" style={{ color: BLUE }}>{fmtEur(costeConIva)}</span></td>
                    <td className="px-3 py-2.5"><span className="font-oranienbaum text-xs" style={{ color: PURPLE }}>{fmtEur(costeConImp)}</span></td>
                    <td className="px-3 py-2.5">
                      <span className="font-oranienbaum text-xs font-bold" style={{ color: getMargenColor(margenReal, margenObj) }}>
                        {margenReal != null ? fmtPct(margenReal) : '—'}
                      </span>
                    </td>
                    <td className="px-3 py-2.5">
                      <span className="font-oranienbaum text-xs" style={{ color: 'rgba(255,255,255,0.40)' }}>
                        {margenObj != null ? fmtPct(margenObj * 100) : '—'}
                      </span>
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-1">
                        <span className="font-oranienbaum text-xs" style={{ color: ACCENT }}>{fmtEur(pvpActual)}</span>
                        {p?.bloqueo_precio && <Icon name="Lock" size={10} style={{ color: BLUE }} />}
                      </div>
                    </td>
                    <td className="px-3 py-2.5">
                      <span className="font-oranienbaum text-xs" style={{ color: pvpSug && pvpActual && Math.abs(pvpSug - pvpActual) > 0.01 ? GOLD : 'rgba(255,255,255,0.35)' }}>
                        {fmtEur(pvpSug)}
                      </span>
                    </td>
                    <td className="px-3 py-2.5">
                      <EpelsaSyncBadge status={p?.epelsa_sync_status} />
                    </td>
                    <td className="px-3 py-2.5">
                      <span className="font-oranienbaum text-xs" style={{ color: stock < 0 ? RED : stock <= (p?.stock_minimo || 0) ? ORANGE : ACCENT }}>
                        {fmt(stock, 0)}
                        {stock < 0 && <span className="ml-1 text-[9px]">⚠</span>}
                      </span>
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-1">
                        <button onClick={() => { setEditProduct(p); setModal('edit'); }} className="p-1.5 rounded-lg text-white/40 hover:text-white/80 transition-colors" title="Editar"><Icon name="Edit2" size={13} /></button>
                        <button onClick={() => setHistoricoProduct(p)} className="p-1.5 rounded-lg text-white/30 hover:text-white/70 transition-colors" title="Histórico costes"><Icon name="History" size={13} /></button>
                        {(p?.epelsa_sync_status === 'PENDIENTE_ALTA' || p?.epelsa_sync_status === 'PENDIENTE_ACTUALIZACION' || p?.epelsa_sync_status === 'PENDIENTE_EPELSA' || p?.epelsa_sync_status === 'ERROR') && (
                          <button
                            onClick={() => handleSincronizarEpelsa(p)}
                            disabled={sincronizandoId === p?.id}
                            className="p-1.5 rounded-lg transition-colors"
                            style={{ color: '#D6EF4E' }}
                            title={p?.epelsa_sync_status === 'ERROR' ? 'Reintentar sincronización Epelsa' : 'Sincronizar con Epelsa'}
                          >
                            {sincronizandoId === p?.id
                              ? <div className="w-3 h-3 rounded-full border border-white/20 border-t-[#D6EF4E] animate-spin" />
                              : <Icon name={p?.epelsa_sync_status === 'ERROR' ? 'RefreshCw' : 'Upload'} size={13} />}
                          </button>
                        )}
                        <button onClick={() => handleToggleActivo(p)} className="p-1.5 rounded-lg transition-colors" style={{ color: p?.activo ? ORANGE : ACCENT }} title={p?.activo ? 'Desactivar' : 'Activar'}><Icon name={p?.activo ? 'EyeOff' : 'Eye'} size={13} /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {!loading && filtered?.length > 0 && (
          <div className="px-4 py-3 flex items-center justify-between" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
            <span className="font-oranienbaum text-white/30 text-xs">{filtered?.length} de {productos?.length} productos</span>
            <span className="font-oranienbaum text-white/30 text-xs">{selected?.size > 0 ? `${selected?.size} seleccionados` : ''}</span>
          </div>
        )}
      </div>

      {/* Modales */}
      {(modal === 'new' || modal === 'edit') && (
        <ProductModal
          product={modal === 'edit' ? editProduct : null}
          familias={familias}
          marcas={marcas}
          proveedores={proveedores}
          onSave={loadAll}
          onClose={() => { setModal(null); setEditProduct(null); }}
        />
      )}
      {modal === 'bulk' && (
        <BulkEditModal
          selectedIds={[...selected]}
          productos={productos}
          familias={familias}
          proveedores={proveedores}
          onSave={() => { setSelected(new Set()); loadAll(); }}
          onClose={() => setModal(null)}
        />
      )}
      {modal === 'familias' && (
        <GestionFamiliasModal onClose={() => setModal(null)} onRefresh={loadAll} />
      )}
      {historicoProduct && (
        <HistoricoModal producto={historicoProduct} onClose={() => setHistoricoProduct(null)} />
      )}
    </div>
  );
};

export default TiendaProductos;
